import React, { useState, useContext, useEffect, useRef } from 'react';
import { toast } from 'sonner';
import axios from 'axios';
import { API, AuthContext } from '@/App';
import ServiceGeofenceMap from '@/components/operations/ServiceGeofenceMap';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter,
} from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { formatPhone } from '@/components/admin/util';
import ImageUploadField from '@/components/operations/ImageUploadField';
import { SearchableSelect, SearchableMultiSelect } from '@/components/common/Combobox';
import { roleLabel } from '@/components/wom/theme';

const LOCATION_TYPES = ['branch', 'headquarters', 'warehouse', 'facility', 'office', 'retail', 'Medical Facility / Clinic', 'other'];
const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const FREQ = ['daily', 'weekly', 'bi-weekly', 'monthly', 'quarterly', 'as-needed'];

const Section = ({ title, children }) => (
  <div className="space-y-3">
    <h4 className="text-xs font-bold uppercase tracking-wider text-[#8FC93A]">{title}</h4>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">{children}</div>
  </div>
);

const F = ({ label, children, full }) => (
  <div className={`space-y-1 ${full ? 'md:col-span-2' : ''}`}>
    <Label className="text-xs text-slate-600">{label}</Label>
    {children}
  </div>
);

const GEOFENCE_DEFAULTS = { enabled: false, latitude: '', longitude: '', arrival_radius_m: 100, departure_radius_m: 200, departure_delay_seconds: 180, max_accuracy_m: 100, short_visit_review_minutes: '' };

const EMPTY = {
  customer_id: '', customer_name: '', location_name: '', building_number: '', industry: '',
  account_number: '', location_code: '', time_zone: '',
  location_type: '', address: '', city: '', state: '', zip: '', county: '', region: '',
  total_sq_ft: '', carpet_sq_ft: '', hard_floor_sq_ft: '', num_floors: '',
  employee_assigned: '', service_frequency: '', service_day: '', service_days: [], service_time: '',
  local_rep_name: '', local_rep_title: '', local_rep_email: '', local_rep_phone: '', local_notes: '',
  tfs_operations_manager_id: '', area_supervisor_id: '', assigned_employee_ids: [], assigned_subcontractor_id: '',
  notes: '', geofence: { ...GEOFENCE_DEFAULTS },
};

const num = (v) => (v === '' || v === null || v === undefined ? null : Number(v));

const LocationForm = ({ location, customers, defaultCustomerId, onClose, onSaved }) => {
  const { token } = useContext(AuthContext);
  const isEdit = !!location;
  const [custList, setCustList] = useState(customers || []);
  const [f, setF] = useState(() => {
    if (location) {
      const clean = Object.fromEntries(Object.entries(location).map(([k, v]) => [k, v == null ? '' : v]));
      return { ...EMPTY, ...clean, geofence: { ...GEOFENCE_DEFAULTS, ...(location.geofence || {}) } };
    }
    return { ...EMPTY, customer_id: defaultCustomerId || '' };
  });
  const [busy, setBusy] = useState(false);
  const [internalUsers, setInternalUsers] = useState([]);
  const [supervisors, setSupervisors] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [subs, setSubs] = useState([]);
  const [activeAssignments, setActiveAssignments] = useState([]);
  useEffect(() => {
    const h = { headers: { Authorization: `Bearer ${token}` } };
    axios.get(`${API}/admin/users?user_type=Internal`, h)
      .then((r) => setInternalUsers((r.data || []).filter((u) => ['owner', 'admin', 'manager', 'tfs_operations'].includes(u.role))))
      .catch(() => {});
    axios.get(`${API}/personnel?type=supervisor&active_only=true`, h)
      .then((r) => setSupervisors(r.data || [])).catch(() => {});
    axios.get(`${API}/personnel?type=employee&active_only=true`, h)
      .then((r) => setEmployees(r.data || [])).catch(() => {});
    axios.get(`${API}/service-providers`, h)
      .then((r) => setSubs(Array.isArray(r.data) ? r.data : (r.data?.items || []))).catch(() => {});
    if (location?.id) {
      axios.get(`${API}/assignments?location_id=${location.id}&status_filter=current`, h)
        .then((r) => setActiveAssignments(Array.isArray(r.data) ? r.data : [])).catch(() => {});
    }
  }, [token]);
  const ROLE_LABEL = { owner: 'Global Administrator', admin: 'General Administrator', manager: 'Operations Manager', tfs_operations: 'Field/Ops Supervisor' };
  const nameTitle = (p) => `${p.name}${p.company_title ? ` — ${p.company_title}` : ''}`;
  const set = (k) => (e) => setF((p) => ({ ...p, [k]: e.target.value }));
  const blurPhone = (k) => () => setF((p) => ({ ...p, [k]: formatPhone(p[k]) }));

  useEffect(() => {
    if (!customers) {
      axios.get(`${API}/customers`, { headers: { Authorization: `Bearer ${token}` } })
        .then((r) => setCustList(r.data || [])).catch(() => {});
    }
  }, []);

  // Snapshot for unsaved-changes detection. Captured after the fresh copy loads (edit) or
  // on mount (create); handleCancel warns before discarding.
  const baselineRef = useRef(null);
  useEffect(() => { if (!location?.id && baselineRef.current === null) baselineRef.current = JSON.stringify(f); }, []);

  // Always hydrate from a FRESH copy on open so the geofence baseline reflects what the
  // backend actually has (the list in LocationManager can be stale after a geofence PATCH).
  useEffect(() => {
    if (!location?.id) return;
    axios.get(`${API}/service-locations/${location.id}`, { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => {
        const fresh = Object.fromEntries(Object.entries(r.data || {}).map(([k, v]) => [k, v == null ? '' : v]));
        setF((p) => {
          const next = { ...p, ...fresh, geofence: { ...GEOFENCE_DEFAULTS, ...(r.data.geofence || {}) } };
          baselineRef.current = JSON.stringify(next);
          return next;
        });
      }).catch(() => {});
  }, [location?.id]);

  const handleCancel = () => {
    if (baselineRef.current && JSON.stringify(f) !== baselineRef.current
      && !window.confirm('You have unsaved changes. Discard them and close?')) return;
    onClose();
  };

  const onCustomerChange = (e) => {
    const id = e.target.value;
    const c = custList.find((x) => x.id === id);
    setF((p) => ({ ...p, customer_id: id, customer_name: c?.name || '', industry: p.industry || c?.industry || '' }));
  };

  const submit = async () => {
    if (!f.customer_id) { toast.error('A customer is required'); return; }
    if (!f.location_name.trim()) { toast.error('Location name is required'); return; }
    if (!f.industry.trim()) { toast.error('Industry is required'); return; }
    for (const k of ['address', 'city', 'state', 'zip', 'county']) {
      if (!String(f[k] || '').trim()) { toast.error(`${k} is required`); return; }
    }
    const g = f.geofence;
    if (g.enabled && (num(g.latitude) === null || num(g.longitude) === null)) { toast.error('Set map coordinates before enabling the geofence'); return; }
    if (num(g.departure_radius_m) < num(g.arrival_radius_m)) { toast.error('Departure radius must be at least the arrival radius'); return; }
    setBusy(true);
    const payload = {
      ...f,
      total_sq_ft: num(f.total_sq_ft), carpet_sq_ft: num(f.carpet_sq_ft),
      hard_floor_sq_ft: num(f.hard_floor_sq_ft), num_floors: num(f.num_floors),
      geofence: Object.fromEntries(Object.entries(f.geofence).map(([k, v]) => [k, k === 'enabled' ? v : num(v)])),
    };
    // Image is managed exclusively by the dedicated upload/delete endpoint. Never let the
    // form Save carry a stale image_url that would clobber a freshly-uploaded image.
    delete payload.image_url;
    try {
      if (isEdit) {
        await axios.put(`${API}/service-locations/${location.id}`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Service location updated');
      } else {
        await axios.post(`${API}/service-locations`, payload, { headers: { Authorization: `Bearer ${token}` } });
        toast.success('Service location created');
      }
      onSaved?.();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not save location');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-3xl max-h-[88vh] overflow-y-auto" data-testid="location-form-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0E2A47]">{isEdit ? `Edit Location — ${location.location_name}` : 'Add Service Location'}</DialogTitle>
        </DialogHeader>

        <div className="sticky top-0 z-20 -mx-6 -mt-2 flex items-center justify-end gap-2 border-b bg-white px-6 pr-12 py-3">
          <button onClick={handleCancel} data-testid="loc-cancel-top" className="rounded-md border border-gray-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="loc-save-top"
            className="rounded-md bg-[#0E2A47] px-5 py-2 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-50">
            {busy ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Location')}
          </button>
        </div>

        <div className="space-y-6 py-2">
          <Section title="Media">
            <div className="md:col-span-2">
              <ImageUploadField
                resource="service-locations" field="image" variant="wide"
                label="Location Image" testidPrefix="loc-image"
                entityId={isEdit ? location.id : null}
                hasImage={!!(location && location.image_url)}
                hint="Shown to Location Requestors in their ROUT portal"
              />
            </div>
          </Section>

          <Section title="Customer & Identity">
            <F label="Customer *">
              <select value={f.customer_id} onChange={onCustomerChange} data-testid="loc-customer"
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm">
                <option value="">Select a customer…</option>
                {custList.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </F>
            <F label="Location Name *"><Input value={f.location_name} onChange={set('location_name')} data-testid="loc-name" /></F>
            <F label="Account #"><Input value={f.account_number} onChange={set('account_number')} data-testid="loc-account-number" /></F>
            <F label="Location Code (4-char)"><Input value={f.location_code} onChange={set('location_code')} maxLength={4} data-testid="loc-code" /></F>
            <F label="Time Zone">
              <select value={f.time_zone || ''} onChange={set('time_zone')} className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm">
                <option value="">—</option>
                {['Eastern Time', 'Central Time', 'Mountain Time', 'Pacific Time'].map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </F>
            <F label="Building / Unit #"><Input value={f.building_number} onChange={set('building_number')} /></F>
            <F label="Industry *"><Input value={f.industry} onChange={set('industry')} data-testid="loc-industry" /></F>
            <F label="Location Type">
              <select value={f.location_type || ''} onChange={set('location_type')}
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm capitalize">
                <option value="">—</option>
                {LOCATION_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </F>
            <F label="Region"><Input value={f.region} onChange={set('region')} /></F>
          </Section>

          <Section title="Address">
            <F label="Address *" full><Input value={f.address} onChange={set('address')} data-testid="loc-address" /></F>
            <F label="City *"><Input value={f.city} onChange={set('city')} data-testid="loc-city" /></F>
            <F label="State *"><Input value={f.state} onChange={set('state')} data-testid="loc-state" /></F>
            <F label="ZIP *"><Input value={f.zip} onChange={set('zip')} data-testid="loc-zip" /></F>
            <F label="County *"><Input value={f.county} onChange={set('county')} data-testid="loc-county" /></F>
          </Section>

          <Section title="Property Details">
            <F label="Total Sq Ft"><Input type="number" value={f.total_sq_ft} onChange={set('total_sq_ft')} /></F>
            <F label="Carpet Sq Ft"><Input type="number" value={f.carpet_sq_ft} onChange={set('carpet_sq_ft')} /></F>
            <F label="Hard Floor Sq Ft"><Input type="number" value={f.hard_floor_sq_ft} onChange={set('hard_floor_sq_ft')} /></F>
            <F label="Floors"><Input type="number" value={f.num_floors} onChange={set('num_floors')} /></F>
          </Section>

          <Section title="Service">
            <F label="Employee Assigned"><Input value={f.employee_assigned} onChange={set('employee_assigned')} /></F>
            <F label="Service Frequency">
              <select value={f.service_frequency || ''} onChange={set('service_frequency')}
                className="w-full rounded-md border border-gray-200 px-3 py-2 text-sm capitalize">
                <option value="">—</option>
                {FREQ.map((t) => <option key={t} value={t}>{t}</option>)}
              </select>
            </F>
            <F label="Service Days">
              <div className="flex flex-wrap gap-x-4 gap-y-2 pt-1" data-testid="loc-service-days">
                {DAYS.map((d) => {
                  const on = (f.service_days || []).includes(d);
                  return (
                    <label key={d} className="flex items-center gap-1.5 text-sm text-slate-600">
                      <input type="checkbox" checked={on} data-testid={`loc-day-${d.toLowerCase()}`}
                        onChange={() => setF((p) => ({ ...p, service_days: on ? p.service_days.filter((x) => x !== d) : [...(p.service_days || []), d] }))} />
                      {d.slice(0, 3)}
                    </label>
                  );
                })}
              </div>
            </F>
            <F label="Service Time"><Input value={f.service_time} onChange={set('service_time')} /></F>
          </Section>

          <Section title="Local Representative & Notes">
            <F label="Local Rep Name"><Input value={f.local_rep_name} onChange={set('local_rep_name')} /></F>
            <F label="Local Rep Title / Position"><Input value={f.local_rep_title} onChange={set('local_rep_title')} data-testid="loc-rep-title" placeholder="e.g., Branch Manager" /></F>
            <F label="Local Rep Email"><Input value={f.local_rep_email} onChange={set('local_rep_email')} /></F>
            <F label="Local Rep Phone"><Input value={f.local_rep_phone} onChange={set('local_rep_phone')} onBlur={blurPhone('local_rep_phone')} /></F>
            <div />
            <F label="Local Notes" full><Textarea rows={2} value={f.local_notes} onChange={set('local_notes')} /></F>
            <F label="Notes" full><Textarea rows={2} value={f.notes} onChange={set('notes')} /></F>
          </Section>

          <Section title="Location Assignments">
            <div className="md:col-span-2 rounded-lg border border-slate-200 bg-slate-50 p-3" data-testid="loc-active-assignments">
              <div className="text-[11px] font-bold uppercase tracking-wider text-slate-500 mb-1.5">Active Assigned Personnel — from Admin Hub Assignments</div>
              {activeAssignments.length ? (
                <ul className="space-y-1">
                  {activeAssignments.map((a) => (
                    <li key={a.id} className="flex items-center justify-between gap-3 text-sm text-slate-700" data-testid={`loc-assignment-${a.id}`}>
                      <span className="font-medium">{a.name || '—'}</span>
                      <span className="text-xs text-slate-500 capitalize">{(a.assignment_role || '').replace(/_/g, ' ') || a.assignment_scope}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-xs text-slate-500">{isEdit ? 'No active personnel assignments for this location. Manage these in Admin Hub › Assignments.' : 'Assignments appear once this location is saved.'}</p>
              )}
              <p className="text-[11px] text-slate-400 mt-2">This is the authoritative roster the TEAM phone app uses to offer this location for a service start. The planning fields below are references and do not control phone access.</p>
            </div>
            <F label="TFS Operations Manager">
              <SearchableSelect testid="loc-ops-manager"
                options={internalUsers.map((u) => ({ value: u.id, label: u.full_name || u.name, sublabel: ROLE_LABEL[u.role] || roleLabel(u.role) }))}
                value={f.tfs_operations_manager_id || ''}
                onChange={(v) => setF((p) => ({ ...p, tfs_operations_manager_id: v }))}
                placeholder="— Unassigned —" />
            </F>
            <F label="Area Supervisor">
              <SearchableSelect testid="loc-area-supervisor"
                options={supervisors.map((s) => ({ value: s.id, label: s.name, sublabel: s.company_title || 'Supervisor' }))}
                value={f.area_supervisor_id || ''}
                onChange={(v) => setF((p) => ({ ...p, area_supervisor_id: v }))}
                placeholder={supervisors.length ? 'Search supervisors…' : 'No supervisors in directory'} />
            </F>
            <F label="Assigned Subcontractor (if applicable)">
              <SearchableSelect testid="loc-subcontractor"
                options={subs.map((s) => ({ value: s.id, label: s.company_name || s.name, sublabel: (s.trade_types || []).join(', ') }))}
                value={f.assigned_subcontractor_id || ''}
                onChange={(v) => setF((p) => ({ ...p, assigned_subcontractor_id: v }))}
                placeholder="— None —" />
            </F>
            <F label="Assigned Employees" full>
              <SearchableMultiSelect testid="loc-assigned-employees"
                options={employees.map((e) => ({ value: e.id, label: e.name, sublabel: e.company_title || 'Employee' }))}
                value={f.assigned_employee_ids || []}
                onChange={(v) => setF((p) => ({ ...p, assigned_employee_ids: v }))}
                placeholder={employees.length ? 'Search employees…' : 'No employees in directory'} />
              {employees.length === 0 && <p className="text-[11px] text-slate-400 mt-1">Add employees in Administration › Personnel.</p>}
            </F>
          </Section>

          <Section title="TEAM — Service Geofence">
            <ServiceGeofenceMap location={f} value={f.geofence} locationId={isEdit ? location.id : null} onChange={geofence => setF(p => ({ ...p, geofence }))} onPersisted={onSaved} />
          </Section>
        </div>

        <DialogFooter className="sticky bottom-0 z-20 -mx-6 -mb-2 gap-2 border-t bg-white px-6 py-3">
          <button onClick={handleCancel} data-testid="loc-cancel" className="rounded-md border border-gray-200 px-4 py-2 text-sm font-medium text-slate-600 hover:bg-gray-50">Cancel</button>
          <button onClick={submit} disabled={busy} data-testid="loc-save"
            className="rounded-md bg-[#0E2A47] px-5 py-2 text-sm font-semibold text-white hover:bg-[#16324F] disabled:opacity-50">
            {busy ? 'Saving…' : (isEdit ? 'Save Changes' : 'Create Location')}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default LocationForm;
